// /**
//  * Parcels details - list
//  */

// React
import React from 'react';

import {Link} from 'react-router-dom';

// UI
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Checkbox,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {mdiCheck, mdiChevronDown, mdiChevronUp, mdiPencil} from '@mdi/js';

// Helper
import {checkMatchMedia} from '../../utils/helpers';

// Type
import {Product} from './interfaces';
import theme from '../../theme';
import handleNotfication from '../../utils/handleNotification';
import productRepository from 'client-sdk/src/Repository/product-repository';
import handleError from '../../utils/handleError';

type Data = Product;
type Props = {
  selectProduct: (id: any) => void;
  data: any;
  selectedProducts: any[];
  selectAll: () => void;
  reloadList: () => void;
};
type Ord = 'asc' | 'desc';

interface HeadCell {
  id: keyof Data;
  label: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
  selectedProducts: any[];
  onSelectAllClick: () => void;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      whiteSpace: 'nowrap',
      color: theme.palette.common.white,
      '@media (max-width: 768px)': {
        fontSize: 10,
      },
    },
    body: {
      whiteSpace: 'nowrap',
      fontSize: 14,
    },
  })
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  })
)(TableRow);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      '& .MuiTableCell-root': {
        padding: '5px',
        '& .MuiButton-text': {
          padding: '0',
        },
      },
    },
    paper: {
      width: '100%',
    },
    table: {
      // minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    textArea: {
      width: '100%',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    columnText: {
      padding: '0 10px',
    },
    container: {
      maxHeight: 540,
      borderRadius: '4px',
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: {[key in Key]: number | string},
  b: {[key in Key]: number | string}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const classes = useStyles();
  const {
    order,
    orderBy,
    onRequestSort,
    rowCount,
    selectedProducts,
    onSelectAllClick,
  } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  const headCells: HeadCell[] = [
    {id: 'name', label: 'نام کالا'},
    {id: 'code', label: 'کد کالا'},
    {id: 'id', label: 'شناسه کالا'},
    {id: 'length', label: 'طول'},
    {id: 'width', label: 'عرض'},
    {id: 'height', label: 'ارتفاع'},
    {id: 'weight', label: 'وزن'},
  ];

  return (
    <TableHead>
      <StyledTableRow>
        <StyledTableCell padding="checkbox">
          <Checkbox
            indeterminate={
              selectedProducts.length > 0 && selectedProducts.length < rowCount
            }
            checked={rowCount > 0 && selectedProducts.length === rowCount}
            onChange={onSelectAllClick}
            inputProps={{'aria-label': 'select all desserts'}}
            style={{color: '#fff'}}
          />
        </StyledTableCell>
        <StyledTableCell />
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              classes={{
                // Override with the active class if this is the selected column or inactive otherwise
                icon:
                  orderBy === headCell.id
                    ? classes.activeSortIcon
                    : classes.inactiveSortIcon,
              }}
              IconComponent={() => (
                <Icon
                  path={
                    orderBy === headCell.id && order === 'desc'
                      ? mdiChevronDown
                      : mdiChevronUp
                  }
                  size={0.7}
                  className={
                    orderBy === headCell.id
                      ? classes.activeSortIcon
                      : classes.inactiveSortIcon
                  }
                />
              )}
            >
              <span className={classes.columnText}>{headCell.label}</span>
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </StyledTableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('productName');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const submit = async (id: any) => {
    const payload = {
      description: '',
      status: 'APPROVED',
    };
    try {
      const response = await productRepository.submitSingleProduct({
        payload,
        id,
      });
      handleNotfication('تایید مشخصات کالا با موفقیت انجام شد', 'success');
      props.reloadList();
    } catch (error) {
      handleError(error.response);
    }
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer className={classes.container}>
          <Table
            stickyHeader
            className={classes.table}
            aria-labelledby="tableTitle"
            size={checkMatchMedia('(max-width: 1024px)') ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={props.data.length}
              selectedProducts={props.selectedProducts}
              onSelectAllClick={props.selectAll}
            />

            <TableBody>
              {stableSort(props.data, getComparator(order, orderBy))
                .slice()
                .map((row: any, index: number) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                  let variantName = row.productName;
                  if (row.featureValues) {
                    for (let i = 0; i <= 2 && row?.featureValues[i]; i++) {
                      variantName = variantName.concat(
                        ' - ',
                        row.featureValues[i].featureName,
                        ' : ',
                        row.featureValues[i].valueName
                      );
                    }
                  }

                  if (row.sellerName || row.providerName) {
                    const sellerName = row.sellerName ?? row.providerName;
                    variantName = variantName.concat(' ( ', sellerName, ' )');
                  }

                  return (
                    <StyledTableRow key={index} hover tabIndex={-1}>
                      {/* suspended */}
                      <TableCell>
                        <Checkbox
                          checked={props.selectedProducts.indexOf(row.id) > -1}
                          onChange={() => props.selectProduct(row.id)}
                          value={row.status}
                          name="submit"
                          aria-label="submit"
                          id="submit"
                        />
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {row.name}
                      </TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {row.code}
                      </TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {row.id}
                      </TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {row.length} سانتی‌متر
                      </TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {row.width} سانتی‌متر
                      </TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {row.height} سانتی‌متر
                      </TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {row.weight} گرم
                      </TableCell>
                      <TableCell align="center" data-cy="row-id">
                        <Link to={`/edit-product/${row.id}`}>
                          <IconButton
                            aria-label="print"
                            size="small"
                            title="ویرایش آیتم"
                            data-cy="print"
                          >
                            <Icon
                              path={mdiPencil}
                              size={1}
                              color={theme.palette.success.main}
                            />
                          </IconButton>
                        </Link>
                        <IconButton
                          aria-label="print"
                          size="small"
                          title="تایید آیتم"
                          data-cy="submit"
                          onClick={() => submit(row.id)}
                        >
                          <Icon
                            path={mdiCheck}
                            size={1}
                            color={theme.palette.success.main}
                          />
                        </IconButton>
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}
